import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { useAudioPlayback } from "@/context/audio-playback-context";

interface MinimalistAudioPlayerProps {
  src: string;
  size?: "sm" | "md" | "lg";
  autoPlay?: boolean;
  audioPlayed: (played: boolean) => void;
}

export function MinimalistAudioPlayer({
  src,
  autoPlay = false,
  size = "md",
  audioPlayed,
}: MinimalistAudioPlayerProps) {
  const { registerPlayer, unregisterPlayer } = useAudioPlayback();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playerId = `minimalist-audio-${src}`;

  // React manages this listener on every render, so no stale closure issues
  const handleEnded = () => {
    setIsPlaying(false);
    unregisterPlayer(playerId);
    audioPlayed(true);
  };

  // Autoplay: wait for canplay if audio hasn't loaded yet to avoid AbortError
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !autoPlay) return;

    const doPlay = () => {
      audio.play()
        .then(() => {
          if (!audio.ended) {
            registerPlayer(playerId);
            setIsPlaying(true);
          }
        })
        .catch((err) => console.error("Error playing audio:", err));
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      doPlay();
    } else {
      audio.addEventListener("canplay", doPlay, { once: true });
      return () => audio.removeEventListener("canplay", doPlay);
    }
  }, [autoPlay, src]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      unregisterPlayer(playerId);
      setIsPlaying(false);
    } else {
      audio.play()
        .then(() => {
          registerPlayer(playerId);
          setIsPlaying(true);
        })
        .catch((err) => console.error("Error playing audio:", err));
    }
  };

  const sizeClasses = { sm: "w-8 h-8", md: "w-10 h-10", lg: "w-12 h-12" };
  const iconSizes = { sm: "h-4 w-4", md: "h-5 w-5", lg: "h-6 w-6" };

  return (
    <div>
      <Button
        variant="outline"
        size="icon"
        onClick={togglePlayPause}
        aria-label={isPlaying ? "Pause" : "Play"}
        className={`rounded-full ${sizeClasses[size]}`}
      >
        {isPlaying ? (
          <Pause className={iconSizes[size]} />
        ) : (
          <Play className={iconSizes[size]} />
        )}
      </Button>
      <audio ref={audioRef} src={src} onEnded={handleEnded}>
        Your browser does not support the audio element.
      </audio>
    </div>
  );
}
